import { Component } from '@angular/core';
import { Title } from '@angular/platform-browser';

interface ValueItem {
  title: string;
  description: string;
}

interface FeatureItem {
  name: string;
  description: string;
}

interface FeatureGroup {
  title: string;
  icon: string;
  summary: string;
  items: FeatureItem[];
}

interface WorkflowStep {
  title: string;
  description: string;
}

interface SpotlightItem {
  title: string;
  headline: string;
  description: string;
  bullets: string[];
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent {
  readonly logoPath = 'assets/apanel/images/logos/logo.svg';

  readonly valueItems: ValueItem[] = [
    {
      title: 'Modular by design',
      description: 'Enable only the domains your team needs, then scale progressively.'
    },
    {
      title: 'Role-aware workflows',
      description: 'Surface relevant tools by access rights to keep operations focused.'
    },
    {
      title: 'Localization-ready',
      description: 'Built for localized routes and language-aware user experiences.'
    },
    {
      title: 'Operational visibility',
      description: 'Track sales, stock, and billing from connected dashboards.'
    }
  ];

  readonly featureGroups: FeatureGroup[] = [
    {
      title: 'Operations',
      icon: 'bx bx-trending-up',
      summary: 'Run the core business loop with inventory, sales, and billing in sync.',
      items: [
        { name: 'Inventory', description: 'Manage products, stocks, categories, brands, variations, and stores.' },
        { name: 'Sales', description: 'Handle orders, customers, and sales dashboards with live metrics.' },
        { name: 'Billing', description: 'Use gateways, invoices, transactions, routing rules, and API keys.' }
      ]
    },
    {
      title: 'Team Productivity',
      icon: 'bx bx-briefcase-alt-2',
      summary: 'Coordinate people, tasks, and workflows across departments.',
      items: [
        { name: 'Task Management', description: 'Organize tasks with boards, states, and assignments.' },
        { name: 'HR', description: 'Support employee-focused workflows and internal operations.' },
        { name: 'CRM', description: 'Track leads, stages, activities, and campaigns end-to-end.' }
      ]
    },
    {
      title: 'Engagement',
      icon: 'bx bx-network-chart',
      summary: 'Drive participation, feedback, and platform activity.',
      items: [
        { name: 'Q/A Platform', description: 'Manage questionnaires, questions, options, and answers.' },
        { name: 'Social Tools', description: 'Handle notifications, feedbacks, and communication touchpoints.' },
        { name: 'Gamification', description: 'Use badges and incentives to encourage user engagement.' }
      ]
    },
    {
      title: 'Digital Commerce & Content',
      icon: 'bx bx-store-alt',
      summary: 'Blend e-commerce presentation with structured web content publishing.',
      items: [
        { name: 'E-Commerce', description: 'Manage top banners and category serial experiences.' },
        { name: 'Web/CMS', description: 'Publish websites, menus, categories, articles, and webpages.' },
        { name: 'Quality & Governance', description: 'Control access, organizations, labels, privileges, and audit records.' }
      ]
    }
  ];

  readonly workflowSteps: WorkflowStep[] = [
    { title: 'Configure', description: 'Set up organization, permissions, catalog, and billing foundations.' },
    { title: 'Operate', description: 'Run day-to-day sales, inventory, tasks, and communication workflows.' },
    { title: 'Optimize', description: 'Use dashboards and domain-level insights to improve outcomes continuously.' }
  ];

  readonly spotlightItems: SpotlightItem[] = [
    {
      title: 'Billing Engine',
      headline: 'Route, collect, and reconcile payments with confidence.',
      description: 'Billing modules support API-driven integrations and operational finance visibility.',
      bullets: ['Gateway and routing rule management', 'Invoice and transaction tracking', 'Template-driven prepared content flows']
    },
    {
      title: 'Inventory + Sales Intelligence',
      headline: 'Turn stock movement into actionable sales decisions.',
      description: 'Inventory and sales dashboards connect operations with measurable business trends.',
      bullets: ['Stock, category, and variation controls', 'Customer and order lifecycle tracking', 'Sales overview and payment analytics']
    },
    {
      title: 'Web + E-Commerce Stack',
      headline: 'Publish content and commerce experiences from one platform.',
      description: 'Combine CMS structures with storefront components for faster campaign delivery.',
      bullets: ['Website and blog composition tools', 'Banner and category serial management', 'Localized content-ready architecture']
    }
  ];

  readonly metrics = [
    { label: 'Domain Areas', value: '14+' },
    { label: 'Operational Modules', value: '50+' },
    { label: 'Core Workflows', value: '100+' }
  ];

  constructor(private title: Title) {
    this.title.setTitle('CognitoX');
  }
}
